import {UpdatableOrganizationFields} from 'src/hooks/useOrganization';
import {UpdatableUserFields} from 'src/hooks/useOrganizationUsers';
import {
  ORG_PRODUCT_HYDROFORECAST,
  ORG_SETTING_MCP_DISABLED,
  ORG_SETTING_PREFIX_HF_FAVORITE_SITES,
  ORG_SETTING_SEASONAL_VOLUME_THRESHOLDS,
} from 'src/lib/constants';
import {Locale} from 'src/lib/translations';

import {getAllPages, makeApiRequest} from './api';

/**
 * Extracting these into named types so it’s clear how the organization
 * settings key-value pair is structured.
 */
type ProjectId = string;
type SiteId = string;

export type ApiOrganizationSettings = {
  [key in `${typeof ORG_SETTING_PREFIX_HF_FAVORITE_SITES}${ProjectId}`]?: SiteId[];
} & {
  locale?: Locale;
  /**
   * Company-level default thresholds (fractions of the long-term average) for the seasonal
   * volume "chance of low/normal/high" chart, e.g. [0.5, 0.75, 0.9]. When unset the chart
   * falls back to DEFAULT_SEASONAL_VOLUME_THRESHOLDS.
   */
  [ORG_SETTING_SEASONAL_VOLUME_THRESHOLDS]?: number[];
  [ORG_SETTING_MCP_DISABLED]?: boolean;
};

export type ApiUserAssociation = {
  id: string; // organization id
  role: 'readonly' | 'regular' | 'admin' | 'owner';
  /** Set when the organization refuses the network this request came from. */
  networkRestricted?: true;
};

export type ApiOrganization = {
  id: string;
  name: string;
  parentOrganizationId: null;
  childOrganizations: ApiOrganization[];
  products: string[];
  settings: ApiOrganizationSettings;
};

export type ApiUserProfile = {
  id: string;
  name: string;
  email: string;
  organizationKey: string;
  organizations: ApiUserAssociation[];
  role: 'readonly' | 'regular' | 'admin' | 'owner';

  /** Set if we deleted the user, so we can keep showing them in the UI temporarily. */
  deletedLocally?: boolean;
  settings: ApiUserSettings;
};

export type ApiSignupToken = {
  token: string;
  role: ApiUserProfile['role'];
  createdAt: string;
  isExpired: string;
  organizationId: string;
  organizationName: string;
};

export enum ApiKeyScope {
  READONLY = 'readonly',
  READWRITE = 'readwrite',
  STREAM_IMAGERY = 'streamimagery',
  EMAIL_OBSERVATIONS = 'emailobservations',
}

export type ApiOrganizationApiKey = {
  key: string;
  scope: ApiKeyScope;
  label: string | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  expired: boolean;
};

export type WeatherDisplay = 'forecastPoint' | 'basinAverage';

export type ApiUserSettings = {
  locale?: Locale;
  /**
   * Which view weather charts default to on the site page. Only takes effect
   * where basin averaging is available; unset behaves like 'forecastPoint'.
   */
  defaultWeatherDisplay?: WeatherDisplay;
};

export async function fetchOrganization(
  organizationId: ApiOrganization['id']
): Promise<ApiOrganization> {
  const response = await makeApiRequest<ApiOrganization>(`organizations/${organizationId}`);
  // makeApiRequest resolves (rather than throwing) on a 4xx/5xx, and an error body has no `data`.
  // Spreading that undefined would yield an ApiOrganization with no id -- which type-checks but
  // blows up later in unrelated code (e.g. getOrgWithIdPrefix). Fail the fetch instead, so the
  // query surfaces an error and callers keep their last good organization.
  if (!response.data) {
    throw new Error(
      `Failed to fetch organization ${organizationId}: ${response.status} ${response.error ?? ''}`
    );
  }
  const childrenResponse = await getAllPages(
    (params) =>
      makeApiRequest<ApiOrganization[]>(
        `organizations/${organizationId}/children`,
        {...params, withProduct: ['HydroForecast']},
        {},
        {noContextOrg: true}
      ),
    {},
    2 // there aren't very many orgs yet so limit number of requests in flight
  );
  return {...response.data, childOrganizations: childrenResponse.data};
}

export async function updateOrganization(
  organizationId: ApiOrganization['id'],
  organizationUpdate: UpdatableOrganizationFields
): Promise<ApiOrganization> {
  const response = await makeApiRequest<ApiOrganization>(
    `organizations/${organizationId}`,
    {},
    {
      method: 'PATCH',
      body: JSON.stringify({
        name: organizationUpdate.name,
        settings: organizationUpdate.settings,
      }),
    }
  );
  return response.data;
}

export async function fetchCurrentUser(): Promise<ApiUserProfile> {
  const response = await makeApiRequest<ApiUserProfile>('user');
  return response.data;
}

export async function fetchOrganizationUsers(
  organizationId: ApiOrganization['id']
): Promise<ApiUserProfile[]> {
  const response = await makeApiRequest<ApiUserProfile[]>(`organizations/${organizationId}/users`);
  return response.data;
}

export async function updateUser(
  uId: ApiUserProfile['id'],
  userUpdate: UpdatableUserFields
): Promise<ApiUserProfile> {
  const response = await makeApiRequest<ApiUserProfile>(
    `user/update_profile`,
    {},
    {
      method: 'PATCH',
      body: JSON.stringify({user_id: uId, name: userUpdate.name}),
    }
  );
  return response.data;
}

export async function changeUserRole(
  uId: ApiUserProfile['id'],
  role: ApiUserProfile['role']
): Promise<ApiUserProfile> {
  const response = await makeApiRequest<ApiUserProfile>(
    `user/update_role`,
    {},
    {
      method: 'PATCH',
      body: JSON.stringify({user_id: uId, role: role}),
    }
  );
  return response.data;
}

export async function deleteUser(uId: ApiUserProfile['id']): Promise<ApiUserProfile> {
  const response = await makeApiRequest<ApiUserProfile>(
    `user/${uId}`,
    {},
    {
      method: 'DELETE',
    }
  );
  return response.data;
}

export async function inviteUser(orgId: string, email: string, role: ApiUserProfile['role']) {
  const response = await makeApiRequest<void>(
    `organizations/${orgId}/invite`,
    {},
    {
      method: 'POST',
      body: JSON.stringify({emails: [email], role, product: ORG_PRODUCT_HYDROFORECAST}),
    }
  );

  if (response.error) {
    throw new Error(response.error);
  }

  return response.data;
}

export async function fetchOrganizationApiKeys(organizationId: ApiOrganization['id']) {
  const response = await makeApiRequest<ApiOrganizationApiKey[]>(
    `organizations/${organizationId}/api_keys`
  );
  return response.data;
}

export async function createApiKey(
  organizationId: ApiOrganization['id'],
  scope: ApiKeyScope,
  expiresAt?: string | null,
  label?: string | null
): Promise<ApiOrganizationApiKey> {
  const response = await makeApiRequest<ApiOrganizationApiKey>(
    `organizations/${organizationId}/api_keys`,
    {},
    {
      method: 'POST',
      body: JSON.stringify({scope, expiresAt: expiresAt ?? null, label: label ?? null}),
    }
  );

  return response.data;
}

export async function updateApiKeyLabel(
  organizationId: ApiOrganization['id'],
  apiKey: string,
  label: string | null
): Promise<ApiOrganizationApiKey> {
  const response = await makeApiRequest<ApiOrganizationApiKey>(
    `organizations/${organizationId}/api_keys/${apiKey}`,
    {},
    {
      method: 'PATCH',
      body: JSON.stringify({label}),
    }
  );

  return response.data;
}

export async function revokeApiKey(
  organizationId: ApiOrganization['id'],
  apiKey: string
): Promise<void> {
  await makeApiRequest<{success: boolean}>(
    `organizations/${organizationId}/api_keys/${apiKey}`,
    {},
    {
      method: 'DELETE',
    }
  );
}

export async function acceptSignup(token: ApiSignupToken['token'], args: {name: string}) {
  await makeApiRequest<void>(`signup/${token}/accept`, null, {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

export async function updateUserSettings(
  settings: ApiUserSettings
): Promise<{id: string; settings: ApiUserSettings}> {
  const response = await makeApiRequest<{id: string; settings: ApiUserSettings}>(
    'user/update_settings',
    {},
    {
      method: 'PATCH',
      body: JSON.stringify(settings),
    }
  );
  return response.data;
}

/**
 * Returns true if the user should see the org switcher UI, either because their
 * profile organization has child organizations or because they belong to multiple organizations.
 */
export function shouldShowOrgSwitcherUI(profileOrganizations: ApiOrganization[]) {
  const profileOrg = profileOrganizations[0];
  if (!profileOrg) {
    return false;
  }

  const hasMultipleProfileOrgs = profileOrganizations.length > 1;
  const profileOrgIsAParentOrg = profileOrg.childOrganizations.length > 0;
  return profileOrgIsAParentOrg || hasMultipleProfileOrgs;
}
